import type { IResultStamp } from "@/api/AppApi/common-interfaces/IResultStamp";
import type {
  IBranch,
  IMapLocation,
  IPaymentMethod,
  ISupportedCurrency,
  DeliveryMethodsTypes,
} from "@/api/AppApi/services/company/interfaces/ICompany";
import type { ITaxSnapshot } from "@/api/AppApi/services/company/interfaces/tax";
import type { IAddOnWithQuantity, IGroupedBundleItems } from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";
import type { DiscountTypes, IProduct } from "../../home/interfaces/IProduct";
import type { unit } from "../../product/interfaces/api-product-details-interface";
export enum CartItemTypes {
  product = "product",
  slot = "slot",
  consultation = "consultation",
  event = "event",
}
export interface IBundleItemsDetails {
  id: 852932;
  name: string;
  sku: string;
  barcode: string;
  type: string;
  image_url: string;
  quantity: number;
  attribute: Record<string, string>;
  price?: number;
}

export interface ICartManageItemParams_Product {
  type: CartItemTypes.product;
  cartId?: number; //used in api /id
  id?: number;
  quantity: number;
  note: string;
  addons: IAddOnWithQuantity[];
  attrs_values: number[];
  other_inputs: object;
  unit_id?: number;
  bundle_items: IGroupedBundleItems[];
}
export interface ICartManageItemParams_Event {
  type: CartItemTypes.event;
  cartId?: number; //used in api /id
  id?: number;
  quantity: number;
  note: string;
  addons: IAddOnWithQuantity[];
  attrs_values: number[];
  other_inputs: object;
  unit_id?: number;
}
export interface IAttachment {
  type: "file" | "voice";
  name: string;
  url: string;
}
export interface ICartManageItemParams_Consult {
  type: CartItemTypes.consultation;
  cartId?: number; //used in api /id
  id: number;
  quantity: number;
  note: string;
  attrs_values: number[];
  message: string;
  files: IAttachment[];
}
export interface ICartManageItemParams_Slot {
  type: CartItemTypes.slot;
  cartId?: number; //used in api /id
  id?: number;
  quantity: number;
  note: string;
  date: string;
  addons?: any[];
  other_inputs: any;
  product_id: number | null;
}
export type ICartManageItemParams = (
  | ICartManageItemParams_Product
  | ICartManageItemParams_Slot
  | ICartManageItemParams_Consult
  | ICartManageItemParams_Event
) & {
  fbc?: string | null;
  fbp?: string | null;
  fb_login_id?: string | null;
  event_id?: string;
  ttp?: string | null;
  ttclid?: string | null;
  twclid?: string | null;
};

export interface IAddOn {
  id: number;
  name: string;
  image: string;
  price: number;
  quantity: number;
  key: string;
}
interface ICartItem {
  taxes?: { id: number; name: string; rate: number }[];
  addons: IAddOn[];
  basic_price: number;
  description: string;
  discount: number;
  can_edit: boolean;
  id: number;
  image_url: string;
  inner_id: number;
  inner_slug?: string;
  name: string;
  note: string;
  price: number;
  quantity: number;
  tax: number;
  thumbnail_url: string;
  type: CartItemTypes;
  unit_id?: number;
  parent: { name: string; sku: string };
  free_quantity: number;
  categories_names?: string[];
}
export interface ICartItem_Slot extends ICartItem {
  type: CartItemTypes.slot;
  others: {
    data_fields: object;
    date: string; //"2023-01-01";
    end_at: string; //"04:30 pm";
    start_at: string; //"03:30 pm";
  };
}
export interface ICartItem_Consultation extends ICartItem {
  type: CartItemTypes.consultation;
  others: {
    data_fields: object;
    attrs_values: [];
    files: [];
    is_new: boolean;
    message: string;
    parent_id: number | null;
  };
}
export interface ICartItem_Event extends ICartItem {
  type: CartItemTypes.event;
  others: {
    data_fields: object;
    attrs_values: [];
    parent_id: number | null;
  };
}
export interface ICartItem_Product extends ICartItem {
  type: CartItemTypes.product;
  bundle_items: IGroupedBundleItems[];
  bundle_items_details: IBundleItemsDetails[];
  others: {
    data_fields: object;
    attrs_values: {
      id: number;
      name: string;
      type: string;
      key: string;
    }[];
    discount_amount: number;
    discount: number;
    discount_type: DiscountTypes;
    is_configurable: boolean;
    is_new: boolean;
    max_quantity_in_cart: number | null;
    min_quantity_in_cart: number | null;
    parent_id: number | null;
    quantity_on_hand: number;
    unit_id: number;
    units: unit[];
    selling_prices: IProduct["selling_prices"];
  };
}
export interface ICoupon {
  code: string;
  description: string;
  discount: number;
  title: string;
}
export type ICartItemType =
  | ICartItem_Product
  | ICartItem_Slot
  | ICartItem_Consultation
  | ICartItem_Event;
export interface IServiceFeeConfig {
  fee_on_customer_rate: number;
  gateway_fixed_fee: number;
  gateway_rate_fee: string | number;
}
export interface IGetCartResult {
  coupon: ICoupon;
  discount: number;
  gateway_account_id: number;
  gateway_id: number;
  items: ICartItemType[];
  price_zone_id: number;
  shipping_charge: number;
  service_fee?: IServiceFeeConfig | number;
  subtotal: number;
  tax: number;
  total: number;
  settings: { available_delivery_types: DeliveryMethodsTypes[] };
}
export interface IGetCart extends IResultStamp {
  result: IGetCartResult;
}
export interface IShippingMethodResult {
  price_zone_id: number;
  name: string;
  icon: string;
  notes: string;
  delivery_fees: number;
  can_set_delivery_date: boolean;
  is_time_required: boolean;
  is_date_required: boolean;
  time_before_order: number;
  delivery_time: number;
  expected_delivery_time: string;
}
export interface IGetSippingMethods extends IResultStamp {
  result: IShippingMethodResult[];
}
export interface ISetPaymentMethodParams {
  gateway_id: number;
  gateway_account_id: number | null;
  country_code: string;
  exchange_grand_total?: number;
  delivery_type?: string;
  fbc?: string | null;
  fbp?: string | null;
  fb_login_id?: string | null;
  event_id?: string;
  ttp?: string | null;
  ttclid?: string | null;
  twclid?: string | null;
}
export interface ISubmitCheckoutResult {
  payment_data: {
    track_id: string;
    result: string; //url
    type: string;
  } | null; //payment method=cash:null, payment method=online:{...}
  invoice_number: string;
  invoice_id: number;
  invoice_date: string;
  invoice_time: string;
  timestamp_invoice_date?: number;
  amount: number;

  track_id?: number;
  payment_method: string;
  order_number: string | number;
  payment_id: string | number;
  reference_number: string | number;
  currency: ISupportedCurrency;
  event_name?: string;
  cities_names?: string[];
  areas_names?: string[];
  countries_names?: string[];
  resource: IResource[];
  currency_code?: string;
}
export interface ISubmitCheckoutResponse extends IResultStamp {
  result: ISubmitCheckoutResult;
}
export enum PaymentResultTypes {
  paid = "paid",
  failed = "failed",
}
export interface IPaymentResult {
  track_id: string;
  invoice_number: string;
  invoice_id: number;
  invoice_date: string;
  invoice_time: string;
  timestamp_invoice_date?: number;
  status: PaymentResultTypes;
  amount: number;
  service_fee?: number;
  payment_info?: string;

  payment_method: string;
  order_number: string | number;
  payment_id: string | number;
  reference_number: string | number;
  resource: IResource[];
  event_name?: string;
  cities_names?: string[];
  areas_names?: string[];
  countries_names?: string[];
  currency_code: string;
}
export interface IPaymentResponse extends IResultStamp {
  result: IPaymentResult;
}

export interface IGetPaymentMethods extends IResultStamp {
  result: IPaymentMethod[];
}

export interface IGetDeliveryBranches extends IResultStamp {
  result: IBranch[];
}

export type PostPaymentMethodAvailable = {
  gateway_account_id: number;
  customer: {
    name: string;
    email: string;
    mobile: {
      number: string;
      country: string;
    };
    other_inputs?: any;
  };
};

export interface IPostPaymentMethodAvailableResult extends IResultStamp {
  result: {
    available: boolean;
    message: string;
  };
}

export interface IWorkingHoursPeriod {
  open_at: string;
  closes_at: string;
}

export interface IWorkingHours {
  day_of_week: string;
  periods: IWorkingHoursPeriod[];
}

export interface IDeliveryBranch {
  id: number;
  name: string;
  mobile: string;
  address: string;
  image_url: string;
  thumbnail_url: string;
  map_location: IMapLocation | null;
  working_hours: IWorkingHours[];
}

export interface IDeliveryAddress {
  id: number;
  mobile: string | null;
  phone: string | null;
  country_id: number;
  country_name: string;
  city_id: number;
  city_name: string;
  area_id: number;
  area_name: string;
  address: string;
  other_address: string;
  is_default: boolean;
  address_name: string;
  contact_name: string;
  other_inputs: Record<string, any>;
  location: string | null;
}

export interface IShippingMethod {
  price_zone_id: number;
  name: string;
  icon: string;
}

export interface IDeliveryInfo {
  is_delivery: boolean;
  delivery_date: string;
  delivery_time: string;
  country_code?: string | null;
  branch?: IDeliveryBranch;
  address?: IDeliveryAddress;
  shipping_method?: IShippingMethod;
}

export interface IItemAttribute {
  id: number;
  name: string;
  type: string;
}

export interface IItemAddon {
  id: number;
  name: string;
  price: number;
  quantity: number;
}

export interface IResourceItem {
  id: number;
  name: string;
  description: string;
  thumbnail: string;
  image_url: string;
  sold_quantity: number;
  base_quantity: number;
  quantity: number;
  price: number;
  discount: number;
  total: number;
  note: string;
  sku: string;
  type: string;
  others: any;
  addons: IItemAddon[];
  attributes: IItemAttribute[];
  basic_price: number;
  taxes_id?: number[];
  taxes?: {
    id: number;
    name: string;
    rate: number;
  }[];
}

export interface IResourceCustomer {
  id: number;
  name: string;
  avatar: string;
  email: string | null;
  mobile: string | null;
}

export interface IResource extends ITaxSnapshot {
  id: number;
  invoice_number: string;
  due_date: string;
  total: number;
  tax?: number;
  unpaid_amount: number;
  delivery_info: IDeliveryInfo;
  status: string;
  order_status: string;
  is_cash_payment: boolean;
  invoice_date: string;
  timestamp_invoice_date?: number;
  items: IResourceItem[];
  items_count: number;
  addons_count: number | null;
  others: any[];
  currency: ISupportedCurrency;
  subtotal: number;
  shipping_charge: number;
  discount: number;
  items_discount: number;
  note: string;
  payment_methods: IPaymentMethod[];
  customer: IResourceCustomer;
  payments: {
    id: number;
    payment_method: string;
    amount: number;
    transaction_id: string;
    reference_number: string;
    payment_id: string;
    status: string;
    date_time: string;
    timestamp_date_time?: number;
  }[];
  can_cancel: boolean;
  can_reorder: boolean;
  can_pay: boolean;
  has_refund: boolean;
  can_refund: boolean;
  has_exchange: boolean;
  can_exchange: boolean;
  exchange_fee: number;
}
